import React from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  CalendarDays,
  CheckCircle2,
  Award,
  X,
  Plus,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function CalendarTab({
  appointments,
  appointmentStats,
  showNewAppointment,
  setShowNewAppointment,
  newAppointment,
  setNewAppointment,
  savingAppointment,
  handleCreateAppointment,
  handleCancelAppointment,
  formatAppointmentTime,
  calendarStatus
}) {
  return (
    <div className="space-y-6">
      {/* Calendar Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
        >
          <div className="flex items-center gap-3 mb-2">
            <CalendarDays className="w-5 h-5 text-emerald-400" />
            <span className="text-slate-400 text-sm">Upcoming</span>
          </div>
          <div className="text-3xl font-bold text-emerald-400">{appointmentStats?.upcoming || 0}</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
        >
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle2 className="w-5 h-5 text-blue-400" />
            <span className="text-slate-400 text-sm">Confirmed</span>
          </div>
          <div className="text-3xl font-bold text-blue-400">{appointmentStats?.confirmed || 0}</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
        >
          <div className="flex items-center gap-3 mb-2">
            <Award className="w-5 h-5 text-purple-400" />
            <span className="text-slate-400 text-sm">Completed</span>
          </div>
          <div className="text-3xl font-bold text-purple-400">{appointmentStats?.completed || 0}</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
        >
          <div className="flex items-center gap-3 mb-2">
            <X className="w-5 h-5 text-red-400" />
            <span className="text-slate-400 text-sm">Cancelled</span>
          </div>
          <div className="text-3xl font-bold text-red-400">{appointmentStats?.cancelled || 0}</div>
        </motion.div>
      </div>

      {/* New Appointment Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Appointments</h2>
        <Button
          onClick={() => setShowNewAppointment(true)}
          className="bg-emerald-500 hover:bg-emerald-600"
          data-testid="new-appointment-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Appointment
        </Button>
      </div>

      {/* New Appointment Form Modal */}
      {showNewAppointment && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-slate-900 border border-slate-700 rounded-xl p-6"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Create New Appointment</h3>
            <button onClick={() => setShowNewAppointment(false)} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Title *</label>
              <input
                type="text"
                value={newAppointment.title}
                onChange={(e) => setNewAppointment({...newAppointment, title: e.target.value})}
                placeholder="e.g., HVAC Repair"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Service Type</label>
              <input
                type="text"
                value={newAppointment.service_type}
                onChange={(e) => setNewAppointment({...newAppointment, service_type: e.target.value})}
                placeholder="e.g., Emergency Repair"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Customer Name *</label>
              <input
                type="text"
                value={newAppointment.customer_name}
                onChange={(e) => setNewAppointment({...newAppointment, customer_name: e.target.value})}
                placeholder="John Smith"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Customer Phone *</label>
              <input
                type="tel"
                value={newAppointment.customer_phone}
                onChange={(e) => setNewAppointment({...newAppointment, customer_phone: e.target.value})}
                placeholder="907-555-1234"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Customer Email</label>
              <input
                type="email"
                value={newAppointment.customer_email}
                onChange={(e) => setNewAppointment({...newAppointment, customer_email: e.target.value})}
                placeholder="john@example.com"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Date & Time *</label>
              <input
                type="datetime-local"
                value={newAppointment.start_time}
                onChange={(e) => setNewAppointment({...newAppointment, start_time: e.target.value})}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-slate-400 mb-1">Notes</label>
              <textarea
                value={newAppointment.notes}
                onChange={(e) => setNewAppointment({...newAppointment, notes: e.target.value})}
                placeholder="Any special instructions..."
                rows={2}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white resize-none"
              />
            </div>
            
            {/* SMS Consent for Appointment */}
            <div className="md:col-span-2 flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <input
                type="checkbox"
                checked={newAppointment.sms_consent || false}
                onChange={(e) => setNewAppointment({...newAppointment, sms_consent: e.target.checked})}
                className="mt-1"
                id="appointment_sms_consent"
                data-testid="appointment-sms-consent"
              />
              <label htmlFor="appointment_sms_consent" className="text-xs text-slate-400">
                Customer consents to receive SMS appointment confirmations and reminders. Message and data rates may apply. Reply STOP to opt-out.
              </label>
            </div>
          </div>

          <div className="flex gap-3 mt-4">
            <Button
              onClick={handleCreateAppointment}
              disabled={savingAppointment}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              {savingAppointment ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Create Appointment
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowNewAppointment(false)}
              className="border-slate-700"
            >
              Cancel
            </Button>
          </div>
        </motion.div>
      )}

      {/* Appointments List */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-800">
          <h2 className="font-semibold text-lg flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-emerald-400" />
            Upcoming Appointments
          </h2>
        </div>

        {appointments.length === 0 ? (
          <div className="text-center py-16">
            <CalendarDays className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-400 mb-2">No appointments yet</h3>
            <p className="text-slate-500 text-sm mb-4">
              Create your first appointment or let your AI book them automatically.
            </p>
            <Button
              onClick={() => setShowNewAppointment(true)}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Appointment
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {appointments.map((apt, index) => (
              <motion.div
                key={apt.appointment_id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.05 }}
                className="px-6 py-4 hover:bg-slate-800/50"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      apt.status === 'confirmed' ? 'bg-emerald-500/20' :
                      apt.status === 'completed' ? 'bg-blue-500/20' :
                      'bg-slate-500/20'
                    }`}>
                      <CalendarDays className={`w-6 h-6 ${
                        apt.status === 'confirmed' ? 'text-emerald-400' :
                        apt.status === 'completed' ? 'text-blue-400' :
                        'text-slate-400'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-medium">{apt.title}</h3>
                      <p className="text-sm text-slate-400">
                        {apt.customer_name} • {apt.customer_phone}
                      </p>
                      <p className="text-sm text-emerald-400">
                        {formatAppointmentTime(apt.start_time)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {apt.service_type && (
                      <span className="px-3 py-1 bg-slate-700 text-slate-300 text-xs rounded-full">
                        {apt.service_type}
                      </span>
                    )}
                    <span className={`px-3 py-1 text-xs rounded-full ${
                      apt.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400' :
                      apt.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                      apt.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {apt.status}
                    </span>
                    {apt.status === 'confirmed' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancelAppointment(apt.appointment_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                {apt.notes && (
                  <p className="mt-2 text-sm text-slate-500 pl-16">
                    Notes: {apt.notes}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Calendar Info */}
      <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Calendar className="w-5 h-5 text-sky-400 mt-0.5" />
          <div>
            <h3 className="font-medium text-sky-400">Built-in Calendar</h3>
            <p className="text-sm text-slate-400 mt-1">
              This is your Alaska AI Services calendar. Appointments booked here are managed within your dashboard.
              {calendarStatus?.connected && (
                <span className="text-emerald-400"> You also have Google Calendar connected for additional sync.</span>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
